// src/contexts/ChatSettingsContext.tsx

import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import { createLogger } from "../utils/logger";
import { useAuth } from "./AuthContext";
import { resolveEntitlements } from "../config/featureEntitlements";

const log = createLogger("ChatSettingsContext");

/**
 * Available chat settings that users can configure
 * This interface is designed to be easily extensible for future features
 */
interface ChatSettings {
  // Extended Thinking feature toggle
  extendedThinking: boolean;

  // Web Search feature toggle
  webSearch: boolean;

  // Composer Mode feature toggle (auto-apply auto-redline edits)
  composerMode: boolean;

  // Benchmark Mode feature toggle (search industry best practices)
  benchmarkMode: boolean;

  // Concise Mode feature toggle (backend injects concise writing style
  // instructions: executive summary + findings table instead of long prose)
  conciseMode: boolean;

  // Tenant ID for multi-tenant testing (session-only)
  tenantId: string;
}

/**
 * Context interface that components can use to interact with chat settings
 * Following the Interface Segregation Principle - only expose what's needed
 */
interface ChatSettingsContextType {
  // Current settings state
  settings: ChatSettings;

  // Setting update methods with clear, focused responsibilities
  toggleExtendedThinking: () => void;
  setExtendedThinking: (enabled: boolean) => void;
  toggleWebSearch: () => void;
  setWebSearch: (enabled: boolean) => void;
  toggleComposerMode: () => void;
  setComposerMode: (enabled: boolean) => void;
  toggleBenchmarkMode: () => void;
  setBenchmarkMode: (enabled: boolean) => void;
  toggleConciseMode: () => void;
  setConciseMode: (enabled: boolean) => void;

  // Utility methods for reading specific settings
  isExtendedThinkingEnabled: () => boolean;
  isWebSearchEnabled: () => boolean;
  isComposerModeEnabled: () => boolean;
  isBenchmarkModeEnabled: () => boolean;
  isConciseModeEnabled: () => boolean;

  // Tenant ID methods (session-only)
  setTenantId: (id: string) => void;
  getTenantId: () => string;

  // Reset all settings to defaults
  resetSettings: () => void;
}

/**
 * Default settings configuration
 * Centralized in one place for easy maintenance and testing
 */
const DEFAULT_SETTINGS: ChatSettings = {
  extendedThinking: true, // Enabled by default
  webSearch: false, // Disabled by default, user can enable
  composerMode: false, // Disabled by default for safety
  benchmarkMode: false, // Disabled by default, user can enable
  conciseMode: false, // Disabled by default, user can enable
  tenantId: "", // Tenant-selector override; "" => no override (the user's own tenant). Session-only.
};

/**
 * Local storage key for persisting settings across sessions
 * Prefixed to avoid conflicts with other app data
 */
const SETTINGS_STORAGE_KEY = "wordaddin_chat_settings";

// Create the context with undefined default to enforce provider usage
const ChatSettingsContext = createContext<ChatSettingsContextType | undefined>(undefined);

/**
 * Props for the ChatSettingsProvider component
 */
interface ChatSettingsProviderProps {
  children: React.ReactNode;
  // Optional initial settings for testing or special configurations
  initialSettings?: Partial<ChatSettings>;
}

/**
 * Settings provider that manages chat configuration state
 *
 * This provider follows several important principles:
 * - Single Responsibility: Only manages settings state
 * - Open/Closed: Easy to extend with new settings without modification
 * - Dependency Inversion: Components depend on this abstraction, not concrete state
 *
 * The provider handles persistence automatically and provides a clean interface
 * for components to interact with settings without knowing implementation details.
 */
export const ChatSettingsProvider: React.FC<ChatSettingsProviderProps> = ({
  children,
  initialSettings = {},
}) => {
  // Initialize settings from localStorage, falling back to defaults
  const [settings, setSettings] = useState<ChatSettings>(() => {
    try {
      // Try to load saved settings from localStorage
      const savedSettings = localStorage.getItem(SETTINGS_STORAGE_KEY);
      if (savedSettings) {
        const parsedSettings = JSON.parse(savedSettings);

        // Merge saved settings with defaults to handle new settings gracefully
        // Always reset session-only settings to defaults
        const mergedSettings = {
          ...DEFAULT_SETTINGS,
          ...parsedSettings,
          ...initialSettings,
          benchmarkMode: false, // Always reset (not persisted)
          tenantId: DEFAULT_SETTINGS.tenantId, // Always reset (not persisted)
        };

        log.info("Loaded chat settings from localStorage (session-only settings reset)", {
          savedSettings: parsedSettings,
          mergedSettings,
          hasInitialOverrides: Object.keys(initialSettings).length > 0,
          sessionOnlyReset: ["benchmarkMode", "tenantId"],
        });

        return mergedSettings;
      }
    } catch (error) {
      // If localStorage is corrupted or unavailable, log and continue with defaults
      log.warn("Failed to load settings from localStorage, using defaults", {
        error: error instanceof Error ? error.message : String(error),
      });
    }

    // Return defaults merged with any initial settings
    const defaultSettings = { ...DEFAULT_SETTINGS, ...initialSettings };

    log.info("Initialized chat settings with defaults", {
      settings: defaultSettings,
      hasInitialOverrides: Object.keys(initialSettings).length > 0,
    });

    return defaultSettings;
  });

  // Where this tenant's thinking toggle starts on each app open. Entitlements
  // arrive from /users/me after this provider mounts - it sits above the auth
  // gate - so the default cannot be read in the initializer above and is
  // applied here instead, once the answer exists.
  //
  // Applied once per mount, which is what "each app open" means, and guarded
  // so a later entitlements change (a re-auth, an account switch) cannot
  // reach back and undo a choice the user has already made this session.
  const { featureEntitlements } = useAuth();
  const [thinkingDefaultOn, setThinkingDefaultOn] = useState(true);
  const appliedThinkingDefault = useRef(false);

  useEffect(() => {
    // Null is what "no answer yet" looks like: AuthContext holds this at null
    // for the whole bootstrap call, and also whenever the access decision is
    // anything but `allowed`. Latching on that would apply the wrong default
    // and then ignore the real one when it lands, so wait for the object.
    if (appliedThinkingDefault.current || !featureEntitlements) {
      return;
    }
    appliedThinkingDefault.current = true;

    const { extendedThinkingDefaultOn } = resolveEntitlements(featureEntitlements);
    setThinkingDefaultOn(extendedThinkingDefaultOn);
    if (extendedThinkingDefaultOn) {
      return; // unchanged for every tenant that has not asked for this
    }

    setSettings((current) => {
      if (!current.extendedThinking) return current;
      log.info("Extended thinking starts off for this tenant", {
        previousValue: current.extendedThinking,
      });
      return { ...current, extendedThinking: false };
    });
  }, [featureEntitlements]);

  // Persist settings to localStorage whenever they change
  // Note: benchmarkMode and tenantId are excluded (session-only, not persisted)
  useEffect(() => {
    try {
      // Exclude session-only settings from localStorage
      const { benchmarkMode, tenantId, ...persistableSettings } = settings;

      // Where the tenant starts thinking off, the value is overridden on every
      // open, so persisting it would leave storage holding a value nothing
      // reads. Dropping it keeps what is stored equal to what is restored.
      if (!thinkingDefaultOn) {
        delete (persistableSettings as Partial<ChatSettings>).extendedThinking;
      }

      localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(persistableSettings));

      log.debug("Persisted settings to localStorage", {
        settings: persistableSettings,
        sessionOnly: ["benchmarkMode", "tenantId"],
      });
    } catch (error) {
      // Handle localStorage errors gracefully (e.g., quota exceeded, private browsing)
      log.error("Failed to persist settings to localStorage", {
        error: error instanceof Error ? error.message : String(error),
        settings,
      });
    }
  }, [settings, thinkingDefaultOn]);

  /**
   * Toggle Extended Thinking setting
   * Provides a convenient method for UI components that need simple toggle behavior
   */
  const toggleExtendedThinking = useCallback(() => {
    setSettings((currentSettings) => {
      const newExtendedThinking = !currentSettings.extendedThinking;

      log.info("Toggled Extended Thinking setting", {
        previousValue: currentSettings.extendedThinking,
        newValue: newExtendedThinking,
      });

      return {
        ...currentSettings,
        extendedThinking: newExtendedThinking,
      };
    });
  }, []);

  /**
   * Set Extended Thinking to a specific value
   * Useful for programmatic control or when you need to set to a specific state
   */
  const setExtendedThinking = useCallback((enabled: boolean) => {
    setSettings((currentSettings) => {
      // Only update if the value is actually changing to avoid unnecessary re-renders
      if (currentSettings.extendedThinking === enabled) {
        log.debug("Extended Thinking setting unchanged", {
          currentValue: enabled,
        });
        return currentSettings;
      }

      log.info("Set Extended Thinking setting", {
        previousValue: currentSettings.extendedThinking,
        newValue: enabled,
      });

      return {
        ...currentSettings,
        extendedThinking: enabled,
      };
    });
  }, []);

  /**
   * Toggle Web Search setting
   * Provides a convenient method for UI components that need simple toggle behavior
   */
  const toggleWebSearch = useCallback(() => {
    setSettings((currentSettings) => {
      const newWebSearch = !currentSettings.webSearch;

      log.info("Toggled Web Search setting", {
        previousValue: currentSettings.webSearch,
        newValue: newWebSearch,
      });

      return {
        ...currentSettings,
        webSearch: newWebSearch,
      };
    });
  }, []);

  /**
   * Set Web Search to a specific value
   * Useful for programmatic control or when you need to set to a specific state
   */
  const setWebSearch = useCallback((enabled: boolean) => {
    setSettings((currentSettings) => {
      // Only update if the value is actually changing to avoid unnecessary re-renders
      if (currentSettings.webSearch === enabled) {
        log.debug("Web Search setting unchanged", {
          currentValue: enabled,
        });
        return currentSettings;
      }

      log.info("Set Web Search setting", {
        previousValue: currentSettings.webSearch,
        newValue: enabled,
      });

      return {
        ...currentSettings,
        webSearch: enabled,
      };
    });
  }, []);

  /**
   * Toggle Composer Mode setting
   * Provides a convenient method for UI components that need simple toggle behavior
   */
  const toggleComposerMode = useCallback(() => {
    setSettings((currentSettings) => {
      const newComposerMode = !currentSettings.composerMode;

      log.info("Toggled Composer Mode setting", {
        previousValue: currentSettings.composerMode,
        newValue: newComposerMode,
      });

      return {
        ...currentSettings,
        composerMode: newComposerMode,
      };
    });
  }, []);

  /**
   * Set Composer Mode to a specific value
   * Useful for programmatic control or when you need to set to a specific state
   */
  const setComposerMode = useCallback((enabled: boolean) => {
    setSettings((currentSettings) => {
      // Only update if the value is actually changing to avoid unnecessary re-renders
      if (currentSettings.composerMode === enabled) {
        log.debug("Composer Mode setting unchanged", {
          currentValue: enabled,
        });
        return currentSettings;
      }

      log.info("Set Composer Mode setting", {
        previousValue: currentSettings.composerMode,
        newValue: enabled,
      });

      return {
        ...currentSettings,
        composerMode: enabled,
      };
    });
  }, []);

  /**
   * Toggle Benchmark Mode setting
   * Provides a convenient method for UI components that need simple toggle behavior
   */
  const toggleBenchmarkMode = useCallback(() => {
    setSettings((currentSettings) => {
      const newBenchmarkMode = !currentSettings.benchmarkMode;

      log.info("Toggled Benchmark Mode setting", {
        previousValue: currentSettings.benchmarkMode,
        newValue: newBenchmarkMode,
      });

      return {
        ...currentSettings,
        benchmarkMode: newBenchmarkMode,
      };
    });
  }, []);

  /**
   * Set Benchmark Mode to a specific value
   * Useful for programmatic control or when you need to set to a specific state
   */
  const setBenchmarkMode = useCallback((enabled: boolean) => {
    setSettings((currentSettings) => {
      // Only update if the value is actually changing to avoid unnecessary re-renders
      if (currentSettings.benchmarkMode === enabled) {
        log.debug("Benchmark Mode setting unchanged", {
          currentValue: enabled,
        });
        return currentSettings;
      }

      log.info("Set Benchmark Mode setting", {
        previousValue: currentSettings.benchmarkMode,
        newValue: enabled,
      });

      return {
        ...currentSettings,
        benchmarkMode: enabled,
      };
    });
  }, []);

  /**
   * Toggle Concise Mode setting
   */
  const toggleConciseMode = useCallback(() => {
    setSettings((currentSettings) => {
      const newConciseMode = !currentSettings.conciseMode;

      log.info("Toggled Concise Mode setting", {
        previousValue: currentSettings.conciseMode,
        newValue: newConciseMode,
      });

      return {
        ...currentSettings,
        conciseMode: newConciseMode,
      };
    });
  }, []);

  /**
   * Set Concise Mode to a specific value
   */
  const setConciseMode = useCallback((enabled: boolean) => {
    setSettings((currentSettings) => {
      if (currentSettings.conciseMode === enabled) {
        log.debug("Concise Mode setting unchanged", {
          currentValue: enabled,
        });
        return currentSettings;
      }

      log.info("Set Concise Mode setting", {
        previousValue: currentSettings.conciseMode,
        newValue: enabled,
      });

      return {
        ...currentSettings,
        conciseMode: enabled,
      };
    });
  }, []);

  /**
   * Check if Extended Thinking is currently enabled
   * Provides a clear, readable method for components that need to check this setting
   */
  const isExtendedThinkingEnabled = useCallback(() => {
    return settings.extendedThinking;
  }, [settings.extendedThinking]);

  /**
   * Check if Web Search is currently enabled
   * Provides a clear, readable method for components that need to check this setting
   */
  const isWebSearchEnabled = useCallback(() => {
    return settings.webSearch;
  }, [settings.webSearch]);

  /**
   * Check if Composer Mode is currently enabled
   * Provides a clear, readable method for components that need to check this setting
   */
  const isComposerModeEnabled = useCallback(() => {
    return settings.composerMode;
  }, [settings.composerMode]);

  /**
   * Check if Benchmark Mode is currently enabled
   * Provides a clear, readable method for components that need to check this setting
   */
  const isBenchmarkModeEnabled = useCallback(() => {
    return settings.benchmarkMode;
  }, [settings.benchmarkMode]);

  /**
   * Check if Concise Mode is currently enabled
   */
  const isConciseModeEnabled = useCallback(() => {
    return settings.conciseMode;
  }, [settings.conciseMode]);

  /**
   * Set tenant ID for multi-tenant testing (session-only, not persisted)
   */
  const setTenantId = useCallback((id: string) => {
    setSettings((currentSettings) => {
      if (currentSettings.tenantId === id) {
        return currentSettings;
      }
      log.info("Set Tenant ID", { previousValue: currentSettings.tenantId, newValue: id });
      return { ...currentSettings, tenantId: id };
    });
  }, []);

  /**
   * Get current tenant ID
   */
  const getTenantId = useCallback(() => {
    return settings.tenantId;
  }, [settings.tenantId]);

  /**
   * Reset all settings to their default values
   * Useful for "reset to defaults" functionality or testing
   */
  const resetSettings = useCallback(() => {
    log.info("Resetting all chat settings to defaults", {
      previousSettings: settings,
      defaultSettings: DEFAULT_SETTINGS,
    });

    setSettings(DEFAULT_SETTINGS);
  }, [settings]);

  // Create the context value with all the methods and state
  const contextValue: ChatSettingsContextType = {
    settings,
    toggleExtendedThinking,
    setExtendedThinking,
    toggleWebSearch,
    setWebSearch,
    toggleComposerMode,
    setComposerMode,
    toggleBenchmarkMode,
    setBenchmarkMode,
    toggleConciseMode,
    setConciseMode,
    isExtendedThinkingEnabled,
    isWebSearchEnabled,
    isComposerModeEnabled,
    isBenchmarkModeEnabled,
    isConciseModeEnabled,
    setTenantId,
    getTenantId,
    resetSettings,
  };

  return (
    <ChatSettingsContext.Provider value={contextValue}>{children}</ChatSettingsContext.Provider>
  );
};

/**
 * Custom hook to use the chat settings context
 *
 * This hook provides a clean interface for components to access settings
 * and enforces that components are wrapped with the provider.
 *
 * @throws Error if used outside of ChatSettingsProvider
 */
export const useChatSettings = (): ChatSettingsContextType => {
  const context = useContext(ChatSettingsContext);

  if (context === undefined) {
    throw new Error(
      "useChatSettings must be used within a ChatSettingsProvider. " +
        "Make sure your component is wrapped with <ChatSettingsProvider>."
    );
  }

  return context;
};

/**
 * Higher-order component for components that need chat settings
 * This provides an alternative to using the hook for class components or special cases
 */
export function withChatSettings<P extends object>(
  Component: React.ComponentType<P & { chatSettings: ChatSettingsContextType }>
): React.FC<P> {
  const WrappedComponent: React.FC<P> = (props) => {
    const chatSettings = useChatSettings();
    return <Component {...props} chatSettings={chatSettings} />;
  };

  WrappedComponent.displayName = `withChatSettings(${Component.displayName || Component.name})`;
  return WrappedComponent;
}
